import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FixedSizeList } from 'react-window';
import { DndContext, closestCenter, pointerWithin, PointerSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import HierarchyNode from './HierarchyNode';
import { DROP_POSITION } from '../constants';
import HierarchyDragLayer from './HierarchyDragLayer';
import { NODE_TYPE } from '../../../PowertrainTools/PowertrainConfigurationV2/Hierarchy/constants';

// Use cursor position for collision detection so DragOverlay size doesn't affect which node is targeted
const collisionDetection = (args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
        return pointerCollisions;
    }
    return closestCenter(args);
};

const HierarchyVirtualList = React.memo(({
    nodes,
    selectedIds,
    expandedIds,
    height,
    itemHeight,
    overscanCount,
    indent,
    onNodeClick,
    onToggleExpand,
    onContextMenu,
    renderNode,
    renderIcon,
    renderActions,
    showExpandIcon,
    showCheckbox,
    getNodeKey,
    isNodeSelectable,
    dragEnabled,
    dropEnabled,
    activeId,
    overId,
    dropPosition,
    onDragStart,
    onDragOver,
    onDragEnd,
    onDragCancel,
    externalNode,
    // Edit mode props
    editMode,
    editingNodeId,
    showDragHandle,
    externalNodeItems,
    isShowExternalNodes,
    isDragging,
    draggedItems,
    onSaveNewNode,
    onCancelNewNode,
    setDropPosition,
}) => {
    const listRef = useRef(null);

    // Configure sensors for drag and drop
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor)
    );

    // Create items array for SortableContext - include both nodes and external nodes
    const items = useMemo(() => {
        const nodeItems = nodes.map(node => getNodeKey(node));
        const externalItems = externalNodeItems ? externalNodeItems.map(node => getNodeKey(node)) : [];
        return [...nodeItems, ...externalItems];
    }, [nodes, externalNodeItems, getNodeKey]);

    // Create selected IDs set for quick lookup
    const selectedIdsSet = useMemo(() => {
        return new Set(selectedIds);
    }, [selectedIds]);

    // Create expanded IDs set for quick lookup
    const expandedIdsSet = useMemo(() => {
        return new Set(expandedIds);
    }, [expandedIds]);

    // Row renderer for react-window
    const Row = useCallback(({ index, style }) => {
        const node = nodes[index];
        if (!node) { return null; }

        const nodeKey = getNodeKey(node);
        const isSelected = selectedIdsSet.has(nodeKey);
        const isExpanded = expandedIdsSet.has(nodeKey);
        const isDragging = activeId === nodeKey;
        const isOver = overId === nodeKey;
        const isEditing = editingNodeId === nodeKey;

        // Check if node should be disabled during drag operations
        let isNodeDisabled = false;
        if (draggedItems && draggedItems.length > 0) {
            const draggedSiteId = draggedItems[0]?.data?.siteId;
            const currentSiteId = node?.data?.siteId;

            if (draggedSiteId && currentSiteId && draggedSiteId !== currentSiteId) {
                isNodeDisabled = true;
            }

            if (!isNodeDisabled && draggedItems[0]?.type === NODE_TYPE.POWERTRAIN && node.type === NODE_TYPE.POWERTRAIN) {
                isNodeDisabled = true;
            }

            if (!isNodeDisabled && draggedItems[0]?.type === NODE_TYPE.ASSET && node.type === NODE_TYPE.POWERTRAIN) {
                isNodeDisabled = true;
            }
        }

        // Disable other nodes when any node has inputEle true
        const hasInputNode = nodes.some(n => n.inputEle === true);
        if (hasInputNode && !node.inputEle) {
            isNodeDisabled = true;
        }

        return (
            <HierarchyNode
                node={{
                    ...node,
                    disabled: isNodeDisabled || node.disabled
                }}
                level={node.level || 0}
                isSelected={isSelected}
                isExpanded={isExpanded}
                isDragging={isDragging}
                isOver={isOver}
                dropPosition={dropPosition}
                indent={indent}
                showExpandIcon={showExpandIcon}
                showCheckbox={showCheckbox}
                onToggleExpand={onToggleExpand}
                onNodeClick={onNodeClick}
                onContextMenu={onContextMenu}
                renderNode={renderNode}
                renderIcon={renderIcon}
                renderActions={renderActions}
                dragEnabled={dragEnabled && !node.inputEle}
                getNodeKey={getNodeKey}
                isNodeSelectable={isNodeSelectable}
                style={style}
                // Edit mode props
                editMode={editMode}
                isEditing={isEditing}
                showDragHandle={showDragHandle}
                onSaveNewNode={onSaveNewNode}
                onCancelNewNode={onCancelNewNode}
                onSetDropPosition={setDropPosition}
                draggedItems={draggedItems}
            />
        );
    }, [
        nodes,
        getNodeKey,
        selectedIdsSet,
        expandedIdsSet,
        activeId,
        overId,
        dropPosition,
        indent,
        showExpandIcon,
        showCheckbox,
        onToggleExpand,
        onNodeClick,
        onContextMenu,
        renderNode,
        renderIcon,
        renderActions,
        dragEnabled,
        isNodeSelectable,
        // Edit mode dependencies
        editMode,
        editingNodeId,
        showDragHandle,
        setDropPosition,
        draggedItems
    ]);

    // Scroll to selected item when selection changes
    useEffect(() => {
        if (listRef.current && selectedIds.length > 0) {
            const firstSelectedId = selectedIds[0];
            const index = nodes.findIndex(node => getNodeKey(node) === firstSelectedId);
            if (index >= 0) {
                listRef.current.scrollToItem(index, 'smart');
            }
        }
    }, [selectedIds, nodes, getNodeKey]);

    // If drag and drop is enabled, wrap in DndContext
    if (dragEnabled && dropEnabled) {
        return (
            <DndContext
                sensors={sensors}
                collisionDetection={collisionDetection}
                onDragStart={onDragStart}
                onDragOver={onDragOver}
                onDragEnd={onDragEnd}
                onDragCancel={onDragCancel}
            >
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                    <FixedSizeList
                        ref={listRef}
                        height={height}
                        itemCount={nodes.length}
                        itemSize={itemHeight}
                        overscanCount={overscanCount}
                        className='hierarchy-virtual-list'
                    >
                        {Row}
                    </FixedSizeList>
                    {isShowExternalNodes &&
                        <>
                            <div className='divider' />
                            {externalNode && React.cloneElement(externalNode, {
                                activeId,
                                overId,
                                dropPosition,
                                indent,
                                renderNode,
                                renderIcon,
                                onContextMenu,
                            })}
                        </>
                    }
                </SortableContext>
                {/* Drag layer for preview */}
                {dragEnabled && isDragging &&
                    <HierarchyDragLayer
                        draggedItems={draggedItems}
                        renderNode={renderNode}
                        renderIcon={renderIcon}
                    />
                }
            </DndContext>
        );
    }

    // Render without drag and drop
    return (
        <FixedSizeList
            ref={listRef}
            height={height}
            itemCount={nodes.length}
            itemSize={itemHeight}
            overscanCount={overscanCount}
            className='hierarchy-virtual-list'
        >
            {Row}
        </FixedSizeList>
    );
});

HierarchyVirtualList.propTypes = {
    nodes: PropTypes.array.isRequired,
    selectedIds: PropTypes.array,
    expandedIds: PropTypes.array,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    itemHeight: PropTypes.number.isRequired,
    overscanCount: PropTypes.number,
    indent: PropTypes.number,
    onNodeClick: PropTypes.func.isRequired,
    onToggleExpand: PropTypes.func.isRequired,
    onContextMenu: PropTypes.func,
    renderNode: PropTypes.func,
    renderIcon: PropTypes.func,
    renderActions: PropTypes.func,
    showExpandIcon: PropTypes.bool,
    showCheckbox: PropTypes.bool,
    getNodeKey: PropTypes.func.isRequired,
    isNodeSelectable: PropTypes.func.isRequired,
    dragEnabled: PropTypes.bool,
    dropEnabled: PropTypes.bool,
    activeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    overId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dropPosition: PropTypes.string,
    onDragStart: PropTypes.func,
    onDragOver: PropTypes.func,
    onDragEnd: PropTypes.func,
    onDragCancel: PropTypes.func,
    externalNode: PropTypes.node,
    editMode: PropTypes.bool,
    editingNodeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    showDragHandle: PropTypes.bool,
    externalNodeItems: PropTypes.array,
    isShowExternalNodes: PropTypes.bool,
    isDragging: PropTypes.bool,
    draggedItems: PropTypes.array,
    onSaveNewNode: PropTypes.func,
    onCancelNewNode: PropTypes.func,
    setDropPosition: PropTypes.func,
};

HierarchyVirtualList.defaultProps = {
    selectedIds: [],
    expandedIds: [],
    overscanCount: 5,
    indent: 20,
    onContextMenu: null,
    renderNode: null,
    renderIcon: null,
    renderActions: null,
    showExpandIcon: true,
    showCheckbox: false,
    dragEnabled: false,
    dropEnabled: false,
    activeId: null,
    overId: null,
    dropPosition: DROP_POSITION.INSIDE,
    onDragStart: null,
    onDragOver: null,
    onDragEnd: null,
    onDragCancel: null,
    externalNode: null,
    editMode: false,
    editingNodeId: null,
    showDragHandle: true,
    externalNodeItems: [],
    isShowExternalNodes: false,
    isDragging: false,
    draggedItems: null,
    onSaveNewNode: null,
    onCancelNewNode: null,
    setDropPosition: null,
};

HierarchyVirtualList.displayName = 'HierarchyVirtualList';

export default HierarchyVirtualList;
